import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { API_BASE_URL } from '../config/api'

interface GalleryImage {
  id: number
  gallery_id?: number
  event_id: number
  image_path?: string
  image_url?: string
  media_url?: string
  is_thumbnail?: number
  title?: string
  created_at?: string
}

interface Event {
  id: number
  title: string
  subtitle?: string
  date: string
  googledrivelink: string
  location: string
  description: string
}

const EventGallery = () => {
  const { eventId } = useParams<{ eventId: string }>()
  const [images, setImages] = useState<GalleryImage[]>([])
  const [event, setEvent] = useState<Event | null>(null)
  const [loading, setLoading] = useState(true)
  const [selectedImage, setSelectedImage] = useState<number | null>(null)

  // Helper function to get full image URL
  const getImageUrl = (image: GalleryImage): string | null => {
    if (image.media_url) return image.media_url
    if (image.image_url) return image.image_url
    if (image.image_path) {
      const cleanPath = image.image_path.replace(/\\/g, '/')
      return `${API_BASE_URL}${cleanPath}`
    }
    return null
  }

  // Fetch event details
  const fetchEvent = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}event_list.php`)
      const result = await response.json()
      
      if (result.status && result.data) {
        const foundEvent = result.data.find((e: Event) => e.id === Number(eventId))
        if (foundEvent) {
          setEvent(foundEvent)
        }
      }
    } catch (err) {
      console.error('Error fetching event:', err)
    }
  }

  // Fetch gallery images for the event
  const fetchGalleryImages = async () => {
    if (!eventId) return
    
    setLoading(true)
    try {
      const response = await fetch(`${API_BASE_URL}event_gallery_list.php?event_id=${eventId}`)
      const result = await response.json()
      
      if (result.status && result.data && result.data.length > 0) {
        setImages(result.data)
      } else {
        setImages([])
      }
    } catch (err) {
      console.error('Error fetching gallery images:', err)
      setImages([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (eventId) {
      fetchEvent()
      fetchGalleryImages()
    }
  }, [eventId])

  const handleImageClick = (index: number) => {
    setSelectedImage(index)
  }

  const closeLightbox = () => {
    setSelectedImage(null)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (selectedImage === null) return
    
    if (e.key === 'ArrowRight' && selectedImage < images.length - 1) {
      setSelectedImage(selectedImage + 1)
    } else if (e.key === 'ArrowLeft' && selectedImage > 0) {
      setSelectedImage(selectedImage - 1)
    } else if (e.key === 'Escape') {
      closeLightbox()
    }
  }

  return (
    <div className="w-full min-h-screen bg-gray-900 py-8 md:py-12">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/events"
            className="inline-flex items-center text-primary-light hover:text-primary-dark font-semibold transition-colors mb-4"
          >
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            Back to Events
          </Link>
          
          {event && (
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-2">
                {event.title}
              </h1>
              {event.subtitle && (
                <p className="text-primary-light text-xl font-semibold mb-2">
                  {event.subtitle}
                </p>
              )}
              <p className="text-gray-400 text-lg mb-4">{event.date}</p>
              {event.location && (
                <p className="text-gray-300 mb-6">{event.location}</p>
              )}
            </div>
          )}
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="text-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-light mx-auto mb-4"></div>
            <p className="text-gray-400 text-lg">Loading gallery...</p>
          </div>
        ) : images.length === 0 ? (
          <div className="text-center py-16">
            <svg className="w-16 h-16 text-gray-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <p className="text-gray-400 text-lg mb-4">No images found for this event.</p>
            {event?.googledrivelink && (
              <a
                href={event.googledrivelink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-primary-light hover:bg-primary-dark text-white px-6 py-3 rounded-lg font-semibold transition-colors"
              >
                View on Google Drive
              </a>
            )}
          </div>
        ) : (
          <>
            {/* Images Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
              {images.map((image, index) => {
                const imageUrl = getImageUrl(image)
                if (!imageUrl) return null
                
                return (
                  <div
                    key={image.id || index}
                    onClick={() => handleImageClick(index)}
                    className="relative aspect-square bg-gray-800 rounded-lg overflow-hidden cursor-pointer group hover:scale-105 transition-transform duration-300"
                  >
                    <img
                      src={imageUrl}
                      alt={image.title || `Gallery Image ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                      <svg className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7" />
                      </svg>
                    </div>
                    {image.is_thumbnail && (
                      <div className="absolute top-2 left-2 bg-primary-light text-white px-2 py-1 rounded text-xs font-semibold">
                        Featured
                      </div>
                    )}
                  </div>
                )
              })}
            </div>

            {/* View More Button */}
            {event?.googledrivelink && (
              <div className="text-center mb-8">
                <a
                  href={event.googledrivelink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center bg-primary-light hover:bg-primary-dark text-white px-8 py-4 rounded-lg font-semibold transition-colors text-lg"
                >
                  <span>View More</span>
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              </div>
            )}
          </>
        )}

        {/* Lightbox Modal */}
        {selectedImage !== null && images[selectedImage] && (
          <div
            className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center p-4"
            onClick={closeLightbox}
            onKeyDown={handleKeyDown}
            tabIndex={0}
          >
            <button
              onClick={closeLightbox}
              className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-10"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            {selectedImage > 0 && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  setSelectedImage(selectedImage - 1)
                }}
                className="absolute left-4 text-white hover:text-gray-300 transition-colors z-10 bg-black/50 rounded-full p-2"
              >
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}
            
            {selectedImage < images.length - 1 && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  setSelectedImage(selectedImage + 1)
                }}
                className="absolute right-4 text-white hover:text-gray-300 transition-colors z-10 bg-black/50 rounded-full p-2"
              >
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            )}

            <div
              onClick={(e) => e.stopPropagation()}
              className="max-w-6xl w-full max-h-[90vh] flex flex-col items-center"
            >
              <img
                src={getImageUrl(images[selectedImage]) || ''}
                alt={images[selectedImage].title || `Gallery Image ${selectedImage + 1}`}
                className="max-w-full max-h-[80vh] object-contain rounded-lg"
              />
              {images[selectedImage].title && (
                <p className="text-white text-center mt-4 text-lg">
                  {images[selectedImage].title}
                </p>
              )}
              <p className="text-gray-400 text-center mt-2 text-sm">
                {selectedImage + 1} of {images.length}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default EventGallery

